import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, map, startWith, of, debounceTime, distinctUntilChanged, switchMap, catchError } from 'rxjs';
import { ContainerType } from 'src/app/data/model/container-type';
import { PortalGroup } from 'src/app/data/model/portal-group';
import { TaggableResourcePickupRoute } from 'src/app/data/model/taggable-resource-pickup-route';
import {COMMA, ENTER} from '@angular/cdk/keycodes';
import { mapURL } from 'src/environments/environment.api';
import * as L from 'leaflet';
import { GeoSearchControl, OpenStreetMapProvider } from 'leaflet-geosearch';
import { ServiceOrder } from 'src/app/data/model/service-order';
import { TaggableResourceList } from 'src/app/data/model/taggable-resource-list';
import { TaggableResourcePickupListApiService } from 'src/app/shared/api/services/taggable-resource-pickup-list-api.service';

export function fileExtensionValidator(allowedExtensions: string[]): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const file = control.value;
    if (file && file.name) {
      const extension = file.name.split('.').pop()?.toLowerCase();
      if (extension && !allowedExtensions.includes(extension)) {
        return { fileExtension: true };
      }
    }
    return null;
  };
}

@Component({
  standalone: false,
  selector: 'app-taggable-resource-route-form',
  templateUrl: './taggable-resource-route-form.component.html',
  styleUrls: ['./taggable-resource-route-form.component.scss']
})
export class TaggableResourceRouteFormComponent implements OnInit {

  @Input() entity!: TaggableResourcePickupRoute | null;
  @Input() waste_types!: ContainerType[] | null;
  @Input() lists!: TaggableResourceList[] | null;
  @Input() loading_icon!: boolean;
  @Input() error!: Map<string, string[]> | null;
  @Input() groups!: PortalGroup[] | null;
  @Input() ods_id!: ServiceOrder[] | null;
  
  optionCtrl = new FormControl();
  separatorKeysCodes: number[] = [ENTER, COMMA];
  @Output() map$: EventEmitter<L.Map>= new EventEmitter;
  @Output() zoom$: EventEmitter<number>=new EventEmitter;
  @Output() destroy= new EventEmitter();

  
  @Output() saveData = new EventEmitter<TaggableResourcePickupRoute>();

  selectedOptions: ContainerType[] = [];
  filteredGroups?: Observable<PortalGroup[]>;
  filteredWasteTypes?: Observable<ContainerType[]>;
  filteredLists?:Observable<TaggableResourceList[]>

  selectedFile: File | null = null;
  base64File: string | null = null;
  input_loading: boolean = false;
  layer = new L.LayerGroup();
  readonly:boolean = false;

  public map!: L.Map;
  zoom!:number;
  static start:L.LatLng | null
  static end:L.LatLng | null

  mapUrl = mapURL.mapUrl !== '${mapURL}' ? mapURL.mapUrl : mapURL.defaultMapUrl;
  @Input() options: L.MapOptions= {
      layers:[L.tileLayer(`${this.mapUrl}/styles/m2openmaptiles/{z}/{x}/{y}.png`, {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    })],
    zoom:5,
    center:L.latLng(45.800084381319905, 13.38134765625),
  };

  public readonly entityFormGroup: FormGroup = this.formBuilder.group({
    id:[null],
    description: [null, [Validators.required, Validators.maxLength(255)]],
    idcli: [null, [ Validators.maxLength(255), Validators.required]],
    group:[null, [Validators.pattern(/\d+/), Validators.required]],
    type:[null, [Validators.required]],
    start_point:[null, [Validators.required]],
    finish_point:[null, [Validators.required]],
    list:[null, [Validators.pattern(/\d+/), Validators.required]],
    file_name: [null]
  })
  
  constructor(
    private formBuilder: FormBuilder,
    private snackBar: MatSnackBar,
    private listService: TaggableResourcePickupListApiService
  ) { }

  

  ngOnInit(): void {
    this.selectedFile = null;
    this.base64File= null;
    this.input_loading = false;
    this.filteredGroups = this.entityFormGroup.get('group')?.valueChanges.pipe(
      startWith(''),
      map((val: string | PortalGroup) => this._filterGroups(val))
    );
    this.filteredWasteTypes = this.entityFormGroup.get('type')?.valueChanges.pipe(
      startWith(''),
      map((val: string | ContainerType) => this._filterWasteTypes(val))
    );
    this.filteredLists = this.entityFormGroup.get('list')!.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(value => this._filterLists(value))
    );
    this.layer.clearLayers();
  }

  setInput(){
    if(this.readonly === false){
      this.readonly=true;
    }
    else if (this.readonly === true){
      this.readonly=false;
    }
  }

  setError() {
    if (this.entityFormGroup.get('file')?.hasError('fileExtension')){
      return { 
        'color': '#fafafa',
        'background-color': '#9c1313',
        'text-shadow': 'black',
        'font-weight': 'bold',
        'border-color': 'black',
        'border-width': 'thin',
        'border-style': 'solid'
      };
    }
    return null
  }
  disableButton(){
    return this.entityFormGroup.get('file')?.hasError('fileExtension') ?? false;
  }

  convertFileToBase64(file: File) {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      this.base64File = reader.result as string;
    };
    reader.onerror = error => {
      console.error('Error: ', error);
    };
  }

  get_coords_from_string(point:string){
    const coords = point.split(" ");
    const mark1= coords[1].split("(");
    const mark2= coords[2].split(")");
    var latlng:L.LatLng 
    if(Number(mark2[0])<Number(mark1[1])){
      latlng = new L.LatLng(Number(mark1[1]), Number(mark2[0]));//(mark1[1], mark2[0]);
    } else {
      latlng = new L.LatLng(Number(mark2[0]), Number(mark1[1]));//(mark1[1], mark2[0]);
    }
    return latlng
  }

  setPoint(start_point: string, finish_point: string): void {
    // Parse the coordinates from the string
    const start_coords = this.get_coords_from_string(start_point);
    const finish_coords = this.get_coords_from_string(finish_point);
  
    // Remove previous markers from the map
    this.layer.clearLayers();
  
    // Define icons for start and end
    const start_icon = L.Icon.extend({
      options: {
        iconSize: [31, 31],
        iconAnchor: [10, 24],
        popupAnchor: [0, -51],
        iconUrl: '../../../../assets/images/start.png'
      }
    });
  
    const end_icon = L.Icon.extend({
      options: {
        iconSize: [31, 31],
        iconAnchor: [10, 24],
        popupAnchor: [0, -51],
        iconUrl: '../../../../assets/images/finish.png'
      }
    });
  
    // Create markers for start and end points
    const start_marker = L.marker(start_coords, { icon: new start_icon() }).bindPopup("Punto di inizio percorso");
    const finish_marker = L.marker(finish_coords, { icon: new end_icon() }).bindPopup("Punto di fine percorso");
  
    // Add markers to the map
    start_marker.addTo(this.layer);
    finish_marker.addTo(this.layer);
  
    // Add layer to map to display markers
    this.layer.addTo(this.map);
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.entityFormGroup.get('description')?.valueChanges.subscribe(() => {
      const nameControl = this.entityFormGroup.get('description');
      if (nameControl) {
        if(this.error){
          nameControl.setErrors(null); 
          this.error=null;
        }
      }
    });
    if (changes['entity']) {
      if(changes['entity'].currentValue){
        this.setPoint(changes['entity'].currentValue.start_point, changes['entity'].currentValue.finish_point);
        if(this.waste_types){
          this.selectedOptions = this.waste_types.filter(option =>
            changes['entity'].currentValue.type.includes(option.id) // Check if 'id' is in 'typeFilterValues'
          ); 
        }
        this.entityFormGroup.patchValue(changes['entity'].currentValue); 
      }
      //this.entityFormGroup.value.type;
    }
    if (this.ods_id){
      this.entityFormGroup.patchValue({idcli:this.ods_id[0].ods_id})
    }
  }

  private _filterLists(value: string | number): Observable<TaggableResourceList[]> {
    if (!value) {
      return of(this.lists?.slice(0, 20) ?? []); // just the first 20
    }
  
    const filterValue = value.toString().toLowerCase();
  
    // Filter from the local list (first 20)
    const localMatches = this.lists?.filter(option => option.name.toLowerCase().includes(filterValue))
      .slice(0, 20) ?? [];
  
    if (localMatches.length > 0) {
      return of(localMatches);
    } else {
      // If no local match, fetch from API
      return this.listService.getWithFilter({"name":filterValue, "limit":5, "offset":0}).pipe(
        map(response => response.results), // adapt based on API response
        catchError(() => of([]))
      );
    }
  }

  private _filterGroups(value: string | PortalGroup): PortalGroup[] {
    if (typeof value === 'string') {
      const search = value.toLowerCase();
      return (this.groups?.filter((group) =>
        group.name.toLowerCase().includes(search)) ?? []
      );
    }
    return [];
  }

  private _filterWasteTypes(value: string | ContainerType): ContainerType[] {
    if (typeof value === 'string') {
      const search = value.toLowerCase();
      return (this.waste_types?.filter((type) =>
        type.waste_type.toLowerCase().includes(search)) ?? []
      );
    }
    return [];
  }

  displayFn = (id: string | number): string => {
    return (
      this.groups?.find((item) => item.id === id)?.name ||
      ''
    );
  };

  displayListFn = (id: string | number): string => {
    return (
      this.lists?.find((item) => item.id === id)?.name ||
      ''
    )
  }



  get availableWasteTypes(): ContainerType[] {
    return this.waste_types?.filter(waste =>
      !this.selectedOptions?.some(selected => selected.id === waste.id)
    ) || [];
  }


  displayTypeFn() {
    return (typeId: number) => {
      const type = this.waste_types?.find((item) => item.id === typeId);
      if (type) {
        var res:string=`${type.type}`;
        if(type.waste_type){
          res=res+` (${type.waste_type})`;
        }
        if(type.capacity){
          res=res+` (${type.capacity})`;
        }
        return res;
      }
      return '';
    }
  }

  addOption(option: ContainerType): void {
    if(this.selectedOptions !== null && this.selectedOptions !== undefined ){
      if (!this.selectedOptions.includes(option)) {
        this.selectedOptions.push(option);
        this.optionCtrl.setValue('');
      }
    } else {
      this.selectedOptions=[];
      this.selectedOptions.push(option);
    }
  }

  emptyOptions(){
    this.entity=null;
    this.selectedOptions=[];
    this.entityFormGroup.value.description=null;
    this.entityFormGroup.value.type=null;
    this.entityFormGroup.reset();
  }

  removeOption(option: ContainerType): void {
    const index = this.selectedOptions.indexOf(option);
    if (index >= 0) {
      this.selectedOptions.splice(index, 1);
    }
  }
  onMapReady(map: L.Map) {
    const start_icon = L.Icon.extend({
      options: {
        iconSize: [31, 31],
        iconAnchor: [10, 24],
        popupAnchor: [0, -51],
        iconUrl: '../../../../assets/images/start.png'
      }
    });

    const end_icon = L.Icon.extend({
      options: {
        iconSize: [31, 31],
        iconAnchor: [10, 24],
        popupAnchor: [0, -51],
        iconUrl: '../../../../assets/images/finish.png'
      }
    });
  
    this.map = map;
    this.map$.emit(map);
    this.zoom = map.getZoom();
    this.zoom$.emit(this.zoom);

    /*const customGeocoder = new (L.Control as any).Geocoder.Nominatim({
      serviceUrl: `${this.mapUrl}/nom/`
    });
    var lastCircle: L.Circle; // Variable to hold reference to the last circle

    const geocoder = (L.Control as any).geocoder({
      defaultMarkGeocode: false,
      geocoder: customGeocoder
    }).on('markgeocode', function(e: any) {
      // Remove the last circle if it exists
      if (lastCircle) {
        map.removeLayer(lastCircle);
      }

      var bbox = e.geocode.bbox;
      const center = bbox.getCenter();
      const radius = center.distanceTo(bbox.getNorthEast());

      // Create a new circle
      lastCircle = L.circle(center, { 
        radius: radius 
      }).addTo(map);

      // Fit map bounds to the new circle
      map.fitBounds(lastCircle.getBounds());
    });


    map.addControl(geocoder);*/
    var lastCircle: L.Circle;
    const customProvider = new OpenStreetMapProvider({
      searchUrl: `${this.mapUrl}/nom/search`,
      reverseUrl: `${this.mapUrl}/nom/reverse`,
      params: {
        format: 'json',
        addressdetails: 1,
        limit: 5,
        countrycodes: 'it'   // optional: restrict to Italy
      }
    });
    
    const searchControl = GeoSearchControl({
      provider: customProvider,
      style: 'button',           // or 'button' for collapsed icon
      autoClose: true,
      keepResult: true,
      searchLabel: 'Cerca indirizzo...',
      notFoundMessage: 'Indirizzo non trovato',
      showMarker: false,      // set true if you want a pin dropped
      showPopup: false,
    });
    
    map.addControl(searchControl);
    
    // Optional: zoom to result like your original code
    map.on('geosearch/showlocation', (e: any) => {
      const { x, y } = e.location;   // lon, lat
      if (lastCircle) map.removeLayer(lastCircle);
      const center = L.latLng(y, x);
      lastCircle = L.circle(center, { radius: 500 }).addTo(map);
      map.fitBounds(lastCircle.getBounds());
    });
  
    //const layer = new L.LayerGroup();
    this.layer.addTo(map);
  
    let clickCount = 0;
    let startPoint: L.LatLng | null = null;
    let endPoint: L.LatLng | null = null;
  
    const startMarker = L.marker([0, 0], { icon: new start_icon() });
    const endMarker = L.marker([0, 0], { icon: new end_icon() });
  
    map.on('click', (e) => {
      const point = new L.LatLng(e.latlng.lat, e.latlng.lng);
  
      if (clickCount === 0) {
        startPoint = point;
        startMarker.setLatLng(point).bindPopup("Punto di inzio percorso");
        this.layer.clearLayers();
        startMarker.addTo(this.layer);
        TaggableResourceRouteFormComponent.start = point
        clickCount++;
      } else if (clickCount === 1) {
        endPoint = point;
        endMarker.setLatLng(point).bindPopup("Punto di fine percorso");
        endMarker.addTo(this.layer);
        TaggableResourceRouteFormComponent.end=endPoint;
        clickCount++;
      } else {
        // Reset for new selection
        clickCount = 0;
        startPoint = null;
        endPoint = null;
        this.layer.clearLayers();
        TaggableResourceRouteFormComponent.start= null;
        TaggableResourceRouteFormComponent.end= null;
      }
    });
  }

  setCoords() {
    if (TaggableResourceRouteFormComponent.start){
      this.entityFormGroup.setValue({...this.entityFormGroup.value, start_point:"SRID=4326;POINT("+TaggableResourceRouteFormComponent.start.lat+" "+TaggableResourceRouteFormComponent.start.lng+")"})
      //this.entityFormGroup.value.coords="SRID=4326;POINT("+TaggableResourceRouteFormComponent.start.lat+" "+TaggableResourceRouteFormComponent.start.lng+")"
    }
    if (TaggableResourceRouteFormComponent.end){
      this.entityFormGroup.setValue({...this.entityFormGroup.value, finish_point:"SRID=4326;POINT("+TaggableResourceRouteFormComponent.end.lat+" "+TaggableResourceRouteFormComponent.end.lng+")"})
      //this.entityFormGroup.value.coords="SRID=4326;POINT("+TaggableResourceRouteFormComponent.end.lat+" "+TaggableResourceRouteFormComponent.end.lng+")"
    }
  }

  
  saveClicked() {
    if(TaggableResourceRouteFormComponent.start && TaggableResourceRouteFormComponent.end){
      this.setCoords();
    }
    if(this.selectedOptions){
      this.entityFormGroup.setValue({...this.entityFormGroup.value, type: this.selectedOptions})
    }
    this.error=null;
    if(this.base64File != null){
      this.entityFormGroup.value.file_name=this.selectedFile?.name
      this.entityFormGroup.value.file=this.base64File
    }
    this.entityFormGroup.markAllAsTouched();

    if (this.entityFormGroup.valid){
      this.saveData.emit(this.entityFormGroup.value);
    } else {
      console.log(this.entityFormGroup.valid)
      console.log(this.entityFormGroup.value)
      console.log(this.entityFormGroup.errors)
      Object.keys(this.entityFormGroup.controls).forEach(key => {
        const control = this.entityFormGroup.get(key);
        console.log(`Control: ${key}, Valid: ${control?.valid}, Errors:`, control?.errors);
      });
    }
  }

  clear(){
    this.entityFormGroup.reset()
    this.entity=null;
    this.destroy.emit();
  }

  onMapZoomEnd(e: L.LeafletEvent) {
    this.zoom = e.target.getZoom();
    this.zoom$.emit(this.zoom);
  }

}
